import { Controller } from "@hotwired/stimulus"

// Renders a single shift's path + response pins on a Mapbox GL canvas.
// Mapbox GL JS is loaded by content_for(:head) on the Signal show view.
export default class extends Controller {
  static targets = ["canvas"]
  static values  = { data: Object }

  connect() {
    if (typeof mapboxgl === "undefined") { this._showFallback("Mapbox not loaded"); return }
    if (!this.hasCanvasTarget) return
    if (!this.dataValue || (!this.dataValue.path?.length && !this.dataValue.start)) {
      this._showFallback("No location data")
      return
    }

    const token = document.querySelector('meta[name="mapbox-token"]')?.content
    if (!token) { this._showFallback("Mapbox token missing"); return }
    mapboxgl.accessToken = token

    const points = (this.dataValue.path || []).map(p => [p.lng, p.lat])
    const center = this.dataValue.current ? [this.dataValue.current[1], this.dataValue.current[0]] :
                   this.dataValue.start   ? [this.dataValue.start[1],   this.dataValue.start[0]]   :
                   points[0]

    this.map = new mapboxgl.Map({
      container: this.canvasTarget,
      style: "mapbox://styles/mapbox/streets-v12",
      center: center,
      zoom: 11,
      attributionControl: false
    })

    this.map.on("load", () => this._draw(points))
  }

  disconnect() {
    if (this.map) this.map.remove()
  }

  _draw(points) {
    if (points.length > 1) {
      this.map.addSource("path", { type: "geojson", data: { type: "Feature", geometry: { type: "LineString", coordinates: points } } })
      this.map.addLayer({ id: "path-line", type: "line", source: "path",
        paint: { "line-color": "#2563eb", "line-width": 3, "line-opacity": 0.8 } })
    }

    (this.dataValue.responses || []).forEach(r => {
      const el = document.createElement("div")
      el.className = "size-3 rounded-full bg-emerald-500 ring-2 ring-white shadow"
      new mapboxgl.Marker({ element: el }).setLngLat([r.lng, r.lat]).addTo(this.map)
    })

    if (this.dataValue.current) {
      const el = document.createElement("div")
      el.className = "size-4 rounded-full bg-sapphire ring-4 ring-white shadow"
      new mapboxgl.Marker({ element: el }).setLngLat([this.dataValue.current[1], this.dataValue.current[0]]).addTo(this.map)
    }

    if (points.length > 1) {
      const bounds = points.reduce((b, p) => b.extend(p), new mapboxgl.LngLatBounds(points[0], points[0]))
      this.map.fitBounds(bounds, { padding: 32, maxZoom: 14 })
    }
  }

  _showFallback(msg) {
    if (!this.hasCanvasTarget) return
    this.canvasTarget.innerHTML = `<div class="flex items-center justify-center h-full text-sm text-slate-400">${msg}</div>`
  }
}
